import type { TaskStatus } from './types';

/**
 * Mirror of the server-side state machine (spec §4). The client uses this
 * for UI affordances ONLY — the database triggers and RPCs are the actual
 * enforcement. If they disagree, the server wins and the UI resyncs.
 *
 *   assigned → accepted → in_transit → arrived → completed
 *                    (any pre-terminal) → skipped
 *
 * ONLY `isTerminal` IS CALLED TODAY (by the route screen, to split finished
 * stops from outstanding ones). The rest described the flow before the 13 Sep
 * rebuild, when a driver walked a stop through accepted → in_transit → arrived
 * by tapping a button at each step. `begin_stop` now does all three in one tap.
 *
 * Kept anyway, deliberately. It is the written form of the state machine the
 * database enforces, it costs nothing at runtime, and "nothing imports it
 * today" is not the same as "safe to delete" -- a status label or a transition
 * check is exactly what the next screen will want. If it is ever removed,
 * remove it because the server's rules changed, not because the grep came back
 * empty.
 */
const DRIVER_TRANSITIONS: Partial<Record<TaskStatus, TaskStatus[]>> = {
  assigned: ['accepted'],
  accepted: ['in_transit'],
  in_transit: ['arrived'],
};

const SKIPPABLE: TaskStatus[] = ['assigned', 'accepted', 'in_transit', 'arrived'];
const TERMINAL: TaskStatus[] = ['completed', 'skipped', 'failed', 'cancelled'];

export const canAdvance = (from: TaskStatus, to: TaskStatus): boolean =>
  DRIVER_TRANSITIONS[from]?.includes(to) ?? false;

export const nextStatus = (from: TaskStatus): TaskStatus | null =>
  DRIVER_TRANSITIONS[from]?.[0] ?? null;

export const canComplete = (s: TaskStatus): boolean => s === 'arrived';
export const canSkip = (s: TaskStatus): boolean => SKIPPABLE.includes(s);
export const isTerminal = (s: TaskStatus): boolean => TERMINAL.includes(s);

export const statusLabel: Record<TaskStatus, string> = {
  assigned: 'Assigned',
  accepted: 'Accepted',
  in_transit: 'In transit',
  arrived: 'Arrived',
  completed: 'Completed',
  skipped: 'Skipped',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

export const actionLabel: Partial<Record<TaskStatus, string>> = {
  assigned: 'Accept stop',
  accepted: 'Start driving',
  in_transit: "I've arrived",
};
